// v4 (V4-SP, spec-v4-extras.md row A10): the web's client for the spending limits the user sets (Settings > Spending)
// and the pure helpers the panel uses. GET /api/me/spending?tz=, PUT /api/me/spending-limits. Under VITE_MOCK the
// data lives in memory here, so the panel works in the mock app too.
import { call } from "./api";

export type SpendingLimits = { daily: number | null; project: number | null; autoOk: number | null; tz: string | null };
export type KindSpend = { kind: string; credits: number };
export type SpendingView = {
  limits: SpendingLimits;
  autoOk: number;
  autoOkDefault: number;
  today: { total: number; byKind: KindSpend[]; left: number | null };
  week: { total: number; byKind: KindSpend[] };
  project?: { id: string; total: number; left: number | null };
};
/** null clears daily / project; autoOk null goes back to the studio's default */
export type LimitsChange = Partial<Record<"daily" | "project" | "autoOk", number | null>>;

const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);
const obj = (v: unknown): Record<string, unknown> => (v && typeof v === "object" ? (v as Record<string, unknown>) : {});
const kinds = (v: unknown): KindSpend[] =>
  (Array.isArray(v) ? v : []).map(obj).filter((k) => typeof k.kind === "string" && num(k.credits) !== null).map((k) => ({ kind: k.kind as string, credits: k.credits as number }));

/** The API's answer, checked (anything missing reads as "nothing spent, no limits"). */
export function normSpending(raw: unknown): SpendingView {
  const o = obj(raw);
  const l = obj(o.limits);
  const t = obj(o.today);
  const w = obj(o.week);
  const autoOkDefault = num(o.autoOkDefault) ?? 50;
  return {
    limits: { daily: num(l.daily), project: num(l.project), autoOk: num(l.autoOk), tz: typeof l.tz === "string" ? l.tz : null },
    autoOk: num(o.autoOk) ?? autoOkDefault,
    autoOkDefault,
    today: { total: num(t.total) ?? 0, byKind: kinds(t.byKind), left: num(t.left) },
    week: { total: num(w.total) ?? 0, byKind: kinds(w.byKind) },
  };
}

/**
 * What the user typed in a limit box: "" = no limit (null), a whole number >= 0, or undefined when it isn't one.
 * For daily / per video, 0 also means no limit (the API reads it that way).
 */
export function parseLimit(text: string, which: "daily" | "project" | "autoOk"): number | null | undefined {
  const t = text.replace(/[,\s]/g, "");
  if (!t) return null;
  if (!/^\d+$/.test(t)) return undefined;
  const n = Number(t);
  if (n > 1_000_000) return undefined;
  return which !== "autoOk" && n === 0 ? null : n;
}

const KIND_WORD: Record<string, string> = {
  frame: "pictures",
  clip: "video clips",
  sfx: "sounds",
  music: "music",
  render: "finished videos",
  script: "scripts",
  director: "Director calls",
  voice_sample: "voice samples",
  sheet: "character sheets",
  character: "character sheets",
};
export const kindWord = (k: string) => KIND_WORD[k] ?? "other";

/** The user's time zone (so "today" is their day), or null. */
export function localTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

// ------------------------------------------------------------------ source (http, or memory under VITE_MOCK)

let mock: SpendingView | null = null;
const isMock = () => import.meta.env?.VITE_MOCK === "1";
const mockView = () =>
  (mock ??= {
    limits: { daily: null, project: null, autoOk: null, tz: localTimeZone() },
    autoOk: 50,
    autoOkDefault: 50,
    today: { total: 85, byKind: [{ kind: "clip", credits: 60 }, { kind: "frame", credits: 15 }, { kind: "director", credits: 10 }], left: null },
    week: { total: 420, byKind: [{ kind: "clip", credits: 300 }, { kind: "frame", credits: 90 }, { kind: "director", credits: 30 }] },
  });

export async function loadSpending(): Promise<SpendingView> {
  if (isMock()) return structuredClone(mockView());
  const tz = localTimeZone();
  return normSpending(await call("GET", `me/spending${tz ? `?tz=${encodeURIComponent(tz)}` : ""}`));
}

export async function saveLimits(change: LimitsChange): Promise<SpendingView> {
  if (isMock()) {
    const v = mockView();
    v.limits = { ...v.limits, ...change };
    v.autoOk = v.limits.autoOk ?? v.autoOkDefault;
    v.today.left = v.limits.daily === null ? null : Math.max(0, v.limits.daily - v.today.total);
    return structuredClone(v);
  }
  return normSpending(await call("PUT", "me/spending-limits", { ...change, tz: localTimeZone() }));
}
